<template>
  <div
    class="iabf-layout"
    :class="{
      'iabf-layout--dancer': isDancerChrome,
      'iabf-dancer-theme': isDancerChrome
    }"
    ref="layoutRoot"
  >
    <!-- Admin: keep existing marketing chrome + sidebar -->
    <template v-if="isAdminRoute">
      <header class="iabf-header">
        <div class="iabf-header-inner">
          <a href="https://www.internationaladultballetfestival.com/" class="iabf-logo-link">
            <img
              :src="adminHeaderBrand.logoUrl"
              :alt="adminHeaderBrand.logoAlt"
              class="iabf-logo"
            />
          </a>
          <button class="mobile-menu-toggle" @click="menuOpen = !menuOpen" aria-label="Toggle menu">
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
          <nav class="iabf-nav" :class="{ open: menuOpen }">
            <a href="https://www.internationaladultballetfestival.com/" @click="menuOpen = false">Home</a>
            <a href="https://www.internationaladultballetfestival.com/about-iabf" @click="menuOpen = false">About IABF</a>
            <a href="https://www.internationaladultballetfestival.com/showcase" @click="menuOpen = false">Perform</a>
            <a href="https://www.internationaladultballetfestival.com/compete" @click="menuOpen = false">Compete</a>
            <a href="https://www.internationaladultballetfestival.com/register" @click="menuOpen = false">Pricing</a>
            <a href="https://www.internationaladultballetfestival.com/policies" @click="menuOpen = false">FAQ</a>
            <router-link to="/" class="nav-dashboard" @click.native="menuOpen = false">My Dashboard</router-link>
          </nav>
        </div>
      </header>
    </template>

    <!-- Dancer app: slim bar (no marketing nav). Hide on public register when logged out. -->
    <DancerAppBar v-else-if="showDancerAppBar" />

    <!-- Masquerade Banner -->
    <div v-if="masqueradeDancer" class="masquerade-banner">
      <span>Viewing as <strong>{{ masqueradeDancer.firstName }} {{ masqueradeDancer.lastName }}</strong></span>
      <button class="masquerade-exit" @click="exitMasquerade">Exit &rarr; Back to Admin</button>
    </div>

    <!-- Content -->
    <main
      class="iabf-main"
      :class="{
        'has-admin-sidebar': isAdminRoute,
        'iabf-main--dancer': isDancerChrome,
        'iabf-main--public-register': (isPublicRegisterRoute && !user) || isRegisterSuccessRoute
      }"
    >
      <AdminSidebar v-if="isAdminRoute" />
      <div class="iabf-main-content">
        <router-view></router-view>
      </div>
    </main>

    <!-- Marketing footer: admin only -->
    <footer v-if="isAdminRoute" class="iabf-footer">
      <div class="iabf-footer-inner">
        <div class="footer-columns">
          <div class="footer-col">
            <a href="https://www.internationaladultballetfestival.com/">Home</a>
            <a href="https://www.internationaladultballetfestival.com/about-iabf">About IABF</a>
            <a href="https://www.internationaladultballetfestival.com/register">Pricing &amp; Registration</a>
            <a href="https://www.internationaladultballetfestival.com/policies">Policies</a>
            <a href="https://www.internationaladultballetfestival.com/contact">Contact</a>
            <router-link to="/">Dancer Dashboard</router-link>
          </div>
          <div class="footer-col footer-locations">
            <p>IABF admin</p>
          </div>
        </div>
        <div class="footer-bottom">
          <a href="https://www.instagram.com/iabf_adultballetfestival/" target="_blank" class="footer-social" aria-label="Instagram">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
          <p class="footer-copyright">Copyright Broche Ballet, LLC</p>
        </div>
      </div>
    </footer>

    <footer v-else class="dancer-footer" :class="{ 'dancer-footer--public': (isPublicRegisterRoute && !user) || isRegisterSuccessRoute }">
      <p>© Broche Ballet, LLC · IABF</p>
    </footer>
  </div>
</template>

<script>
import AdminSidebar from '@/components/shared/AdminSidebar'
import DancerAppBar from '@/components/iabf/DancerAppBar'
import { applyIabfBrand, getIabfBrand, IABF_BRAND_YEAR } from '@/config/iabf-brand'
import '@/styles/iabf-fonts.css'
import '@/styles/iabf-dancer-theme.css'

export default {
  components: { AdminSidebar, DancerAppBar },
  data () {
    return {
      menuOpen: false
    }
  },
  computed: {
    masqueradeDancer () {
      return this.$store.getters.masqueradeDancer
    },
    user () {
      return this.$store.getters.user
    },
    isAdminRoute () {
      return (this.$route.path || '').startsWith('/iabf/admin')
    },
    adminHeaderBrand () {
      const y = Number(this.$store.getters.adminFestivalYear)
      if (y === 2026 || y === 2027) {
        return getIabfBrand(y)
      }
      if (y && y < 2027) {
        return getIabfBrand(2026)
      }
      return getIabfBrand(2027)
    },
    isPublicRegisterRoute () {
      const p = this.$route.path || ''
      return p === '/register' || p.startsWith('/register/') ||
        p === '/iabf/register' || p.startsWith('/iabf/register/')
    },
    isRegisterSuccessRoute () {
      const p = this.$route.path || ''
      return p === '/register/success' || p.startsWith('/register/success') ||
        p === '/iabf/register/success' || p.startsWith('/iabf/register/success')
    },
    /** App bar: hide on public registration when logged out, and on the post-register celebration. */
    showDancerAppBar () {
      if (this.isAdminRoute) {
        return false
      }
      if (this.isRegisterSuccessRoute) {
        return false
      }
      if (this.isPublicRegisterRoute && !this.user) {
        return false
      }
      return true
    },
    isDancerChrome () {
      // Dancer shell for all non-admin IABF routes (including style-guide)
      return !this.isAdminRoute
    },
    /** Year whose Firestore deadline doc should drive is*Closed helpers. */
    deadlineYear () {
      if (this.isAdminRoute) {
        return this.$store.getters.adminFestivalYear
      }
      return this.$store.getters.iabfDancerYear
    }
  },
  watch: {
    isDancerChrome: {
      immediate: true,
      handler (on) {
        if (on) {
          this.$nextTick(() => this.applyBrand())
        }
      }
    },
    deadlineYear: {
      immediate: true,
      handler (y) {
        if (!y) {
          return
        }
        this.$store.dispatch('loadIabfDeadlinesConfig', y).catch(() => {})
      }
    }
  },
  mounted () {
    if (this.isDancerChrome) {
      this.applyBrand()
    }
  },
  methods: {
    applyBrand () {
      const el = this.$refs.layoutRoot
      applyIabfBrand(IABF_BRAND_YEAR, el || undefined)
    },
    exitMasquerade () {
      this.$store.commit('setMasqueradeDancer', null)
      this.$router.push('/iabf/admin')
    }
  }
}
</script>

<style scoped>
.iabf-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.iabf-layout--dancer {
  background: var(--iabf-bg, #f7f6f4);
}

/* Header (admin) */
.iabf-header {
  border-bottom: 1px solid #f5d6a8;
  background: linear-gradient(180deg, #fef5f0 0%, #fef9ec 100%);
  position: sticky;
  top: 0;
  z-index: 100;
}

.iabf-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.iabf-logo-link {
  display: flex;
  align-items: center;
}

.iabf-logo {
  height: 50px;
  width: auto;
}

.iabf-nav {
  display: flex;
  align-items: center;
  gap: 28px;
}

.iabf-nav a {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  color: #1a1a1a;
  text-decoration: none;
  letter-spacing: .02em;
  transition: color 0.15s;
}

.iabf-nav a:hover {
  color: #666;
}

.nav-dashboard {
  padding: 8px 18px;
  border: 1px solid #1a1a1a;
  transition: background 0.2s, color 0.2s;
}

.nav-dashboard:hover {
  background: #1a1a1a;
  color: #fff !important;
}

.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}

.hamburger-line {
  display: block;
  width: 22px;
  height: 1.5px;
  background: #1a1a1a;
}

/* Masquerade banner */
.masquerade-banner {
  background: #b45309;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
  font-family: var(--iabf-font-body, 'Fanwood Text', Georgia, serif);
  font-size: 15px;
  position: relative;
  z-index: 101;
}

.masquerade-exit {
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.5);
  color: #fff;
  padding: 4px 14px;
  border-radius: 3px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
}

.masquerade-exit:hover {
  background: rgba(255,255,255,0.3);
}

/* Main */
.iabf-main {
  flex: 1;
}

.iabf-main--dancer {
  padding: 0;
}

.iabf-main.has-admin-sidebar {
  display: grid;
  grid-template-columns: 240px 1fr;
  align-items: start;
}

.iabf-main-content {
  min-width: 0;
}

.iabf-main.has-admin-sidebar .iabf-main-content {
  overflow-x: auto;
}

@media (max-width: 800px) {
  .iabf-main.has-admin-sidebar {
    grid-template-columns: 1fr;
  }
}

/* Admin footer */
.iabf-footer {
  background: #dd4762;
  color: #fff5ec;
  margin-top: 40px;
}

.iabf-footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 32px;
}

.footer-columns {
  display: flex;
  gap: 60px;
  margin-bottom: 32px;
}

.footer-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-col a {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  color: #fff5ec;
  text-decoration: none;
}

.footer-locations p {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  color: #fde4d3;
  margin: 0;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 245, 236, 0.25);
  padding-top: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-social {
  color: #fff5ec;
}

.footer-copyright {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  font-style: italic;
  color: #fde4d3;
  margin: 0;
}

.dancer-footer {
  padding: 24px 16px 32px;
  text-align: center;
}

.dancer-footer p {
  margin: 0;
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  font-family: var(--iabf-font-body, inherit);
}

@media (max-width: 768px) {
  .mobile-menu-toggle {
    display: flex;
  }

  .iabf-nav {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    flex-direction: column;
    padding: 16px 24px 24px;
    gap: 16px;
    border-bottom: 1px solid #e8e5e1;
    box-shadow: 0 4px 12px rgba(0,0,0,.06);
  }

  .iabf-nav.open {
    display: flex;
  }

  .footer-columns {
    flex-direction: column;
    gap: 32px;
  }
}

@media print {
  .iabf-header,
  .iabf-footer,
  .dancer-footer,
  .masquerade-banner {
    display: none !important;
  }
  .iabf-main.has-admin-sidebar {
    grid-template-columns: 1fr;
  }
  .iabf-main.has-admin-sidebar > :first-child:not(.iabf-main-content) {
    display: none !important;
  }
}
</style>
